import {
    ActionBar,
    BottomInfoBar,
    BottomInfoElement,
    Description,
    DialogWrapper,
    InfoText,
    Tags,
    TagWrapper
} from './FolderDetailsDialog.style';
import React, {useContext, useState} from 'react';
import {Button, Icon} from '@bosch/react-frok';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {t} from 'i18next';
import {formatDate, formatSize} from '../../../utils';
import {getLabel} from '../../../utils/StorageObjectUtils';
import {AuthContext} from '../../../context/AuthContext';
import {hasEditorialAccess} from '../../../utils/UserUtils';

export interface FolderDetailsDialogProps {
    readonly item: StorageObject;
    readonly language: string;
    readonly defaultLocale: string;
    readonly editMode: boolean;
    editAction: () => void;
    deleteAction: () => void;
    onClose: () => void;
}

/**
 * @name FolderDetailsDialog
 * @description
 * The dialog containing information to the folder that has been clicked
 */
export const FolderDetailsDialog: React.VFC<FolderDetailsDialogProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isEditorialAccess] = useState<boolean>(hasEditorialAccess(isAuthenticated, roles));
    let label = getLabel(props.defaultLocale, props.item.labels);

    return (
        <DialogWrapper
            modal
            open
            title={t('FOLDER_DETAILS_DIALOG_TITLE')}
            headlineSpace={props.editMode}
            headline={label.name ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')}
            onClose={props.onClose}
        >
            {props.editMode && (
                <ActionBar
                    key={'command-bar'}
                    actions={[
                        <Button
                            key='edit'
                            icon='edit'
                            mode='tertiary'
                            type='button'
                            onClick={props.editAction}
                        />,
                        <Button
                            key='delete'
                            icon='delete'
                            mode='tertiary'
                            type='button'
                            onClick={props.deleteAction}
                        />
                    ]}
                />
            )}

            <InfoText>
                {t('FOLDER_META_DATA_DIALOG_ORIGINAL_NAME_INFO_TEXT', {name: props.item.name ? props.item.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
            </InfoText>

            <InfoText>
                {t('FOLDER_DETAILS_DIALOG_FOLDER_NAME_TEXT', {name: label.name ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
            </InfoText>

            {isEditorialAccess &&
            <InfoText>
                {t('FOLDER_DETAILS_PUBLIC_ACCESS', {isPublic: props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')})}
            </InfoText>
            }

            <InfoText>
                {t('FOLDER_DETAILS_DIALOG_DATE_CREATED_INFO_TEXT', {date: props.item.metadata.dateCreated ? formatDate(props.language, props.item.metadata.dateCreated) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
            </InfoText>

            <InfoText>
                {t('FOLDER_DETAILS_DIALOG_DATE_UPDATED_INFO_TEXT', {date: props.item.metadata.dateUpdated ? formatDate(props.language, props.item.metadata.dateUpdated) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
            </InfoText>

            <InfoText>
                {t('DATE_FROM_INFO_TEXT', {date: props.item.metadata.dateValidFrom ? formatDate(props.language, props.item.metadata.dateValidFrom) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
            </InfoText>

            <InfoText>
                {t('DATE_TO_INFO_TEXT', {date: props.item.metadata.dateValidTo ? formatDate(props.language, props.item.metadata.dateValidTo) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
            </InfoText>

            <Description>
                {t('DESCRIPTION_INFO_TEXT', {description: label.description ? label.description : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
            </Description>

            <TagWrapper>
                {props.item.metadata.tags?.map(tag => {
                    return <Tags key={tag} label={tag}/>;
                })}
            </TagWrapper>

            <BottomInfoBar>
                {(props.item.objectType === ObjectType.FOLDER) && (
                    <BottomInfoElement>
                        <Icon iconName='folder'/>
                        {props.item.metadata.folderCount ?? 0}
                    </BottomInfoElement>
                )}
                {(props.item.objectType === ObjectType.FOLDER) && (
                    <BottomInfoElement>
                        <Icon iconName='list-view'/>
                        {props.item.metadata.fileCount ?? 0}
                    </BottomInfoElement>
                )}
                <BottomInfoElement>{formatSize(props.item.totalSize)}</BottomInfoElement>
            </BottomInfoBar>
        </DialogWrapper>
    );
};
